// Implements: F-UIX-020
/**
 * The live contrast of every declared pair, computed in the browser with the
 * server's formula (`shared/ui/contrast.ts`), so changing a colour updates the
 * ratio with no request. A colour that is not one yet (half typed) has no
 * ratio and counts as failing: the save stays off until every pair is known
 * and reaches 4.5:1.
 */

import { contrastRatio, meetsAA } from "../../../shared/ui/contrast";
import { PLATFORM_LOCALE } from "../../../types/locale";
import type { PaletteValues } from "../../../types/design";
import { CONTRAST_PAIRS } from "../model/profileModel";
import type { PairName } from "../model/profileModel";
import type { DesignKey } from "./editorOptions";

export interface PairResult {
  readonly name: PairName;
  readonly label: DesignKey;
  /** `null` when one of the two colours is not a valid colour. */
  readonly ratio: number | null;
  readonly passes: boolean;
}

const PAIR_LABELS: Readonly<Record<PairName, DesignKey>> = {
  "text-on-surface": "design.pair.textOnSurface",
  "surface-on-primary": "design.pair.surfaceOnPrimary",
  "text-on-secondary": "design.pair.textOnSecondary",
  "success-on-surface": "design.pair.successOnSurface",
  "warning-on-surface": "design.pair.warningOnSurface",
  "danger-on-surface": "design.pair.dangerOnSurface",
};

/** The dictionary entry of a pair, so a refusal names it with the words of the live list. */
export function pairLabel(name: PairName): DesignKey {
  return PAIR_LABELS[name];
}

export function evaluatePairs(palette: PaletteValues): readonly PairResult[] {
  return CONTRAST_PAIRS.map((pair) => {
    const ratio = contrastRatio(palette[pair.foreground], palette[pair.background]);
    return {
      name: pair.name,
      label: pairLabel(pair.name),
      ratio,
      passes: ratio !== null && meetsAA(ratio),
    };
  });
}

const RATIO = new Intl.NumberFormat(PLATFORM_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * `4.49:1` in the platform locale (period decimals, like the money): truncated, never rounded, to two
 * decimals, so a failing 4.497 can never be displayed as a passing-looking `4.50`.
 */
export function formatRatio(ratio: number): string {
  return `${RATIO.format(Math.floor(ratio * 100) / 100)}:1`;
}
